import React, { useEffect, useState, useCallback } from "react";
import { Ghost, Loader2, Trash2, Archive, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

/**
 * Phase 101bo — Nettoyage des plats fantômes.
 *
 * Détecte et purge de `distributor.displayed_product_rails` +
 * `displayed_product_ids` les références vers des produits archivés
 * (soft-deleted) ou inexistants.
 *
 * Sans impact HACCP : les lots, mouvements et données Nayax restent intacts.
 * Purement du nettoyage de mapping d'affichage.
 */
export default function PhantomRailsDialog({ open, distributor, onClose, onCleaned }) {
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!distributor?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/distributors/${distributor.id}/phantom-rails`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [distributor?.id]);

  useEffect(() => {
    if (open) {
      load();
    } else {
      setData(null);
    }
  }, [open, load]);

  const doClean = async () => {
    if (!distributor?.id) return;
    setCleaning(true);
    try {
      const r = await api.post(`/distributors/${distributor.id}/clean-phantom-rails`);
      toast.success(
        r.data.removed_count > 0
          ? `${r.data.removed_count} plat${r.data.removed_count > 1 ? "s" : ""} fantôme${r.data.removed_count > 1 ? "s" : ""} nettoyé${r.data.removed_count > 1 ? "s" : ""}`
          : "Aucun plat fantôme à nettoyer"
      );
      onCleaned?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de nettoyage");
    } finally {
      setCleaning(false);
    }
  };

  const phantoms = data?.phantoms || [];
  const hasPhantoms = phantoms.length > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="phantom-rails-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ghost className="w-5 h-5 text-[#8C6B2F]" />
            Plats fantômes — {distributor?.name}
          </DialogTitle>
          <DialogDescription>
            Références orphelines dans <code className="text-xs bg-[#F1EEE7] px-1 py-0.5 rounded">displayed_product_rails</code> pointant vers des produits archivés ou introuvables. Sans impact HACCP, ni sur les lots ou Nayax.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-[180px]">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-[#575A57]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Analyse des rails…
            </div>
          ) : !hasPhantoms ? (
            <div className="text-center py-8 text-[#1F4E3D]" data-testid="phantom-rails-empty">
              <div className="text-4xl mb-2">✓</div>
              <div className="font-medium">Aucun plat fantôme détecté</div>
              <div className="text-xs text-[#575A57] mt-1">
                {data?.total_rails_entries ?? 0} entrée{(data?.total_rails_entries ?? 0) > 1 ? "s" : ""} de rail{(data?.total_rails_entries ?? 0) > 1 ? "s" : ""} — toutes valides.
              </div>
            </div>
          ) : (
            <div className="space-y-2 max-h-[380px] overflow-y-auto pr-2">
              <div className="text-sm text-[#575A57] mb-2">
                <strong className="text-[#C05C47]">{phantoms.length}</strong> référence{phantoms.length > 1 ? "s" : ""} orpheline{phantoms.length > 1 ? "s" : ""} détectée{phantoms.length > 1 ? "s" : ""} :
              </div>
              {phantoms.map((p) => (
                <div
                  key={p.product_id}
                  className="flex items-center justify-between px-3 py-2 bg-[#FBF4E9] border border-[#E8D3A8] rounded-md"
                  data-testid={`phantom-row-${p.product_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                      {p.product_name || <span className="italic text-[#8C6B2F]">Produit introuvable</span>}
                    </div>
                    <div className="text-xs text-[#575A57] font-mono truncate">{p.product_id}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    {p.slots.length > 0 && (
                      <div className="flex gap-1">
                        {p.slots.map((s) => (
                          <Badge
                            key={s}
                            variant="outline"
                            className="bg-white border-[#575A57] text-[#575A57] font-mono text-xs"
                          >
                            {s}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {p.reason === "archived" ? (
                      <Badge className="bg-[#8C6B2F] text-white gap-1">
                        <Archive className="w-3 h-3" /> Archivé
                      </Badge>
                    ) : (
                      <Badge className="bg-[#C05C47] text-white gap-1">
                        <HelpCircle className="w-3 h-3" /> Introuvable
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={cleaning} data-testid="phantom-cancel-btn">
            {hasPhantoms ? "Annuler" : "Fermer"}
          </Button>
          {hasPhantoms && (
            <Button
              onClick={doClean}
              disabled={cleaning || loading}
              className="bg-[#C05C47] hover:bg-[#a94c3a] text-white gap-2"
              data-testid="phantom-clean-btn"
            >
              {cleaning ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Nettoyer {phantoms.length} plat{phantoms.length > 1 ? "s" : ""} fantôme{phantoms.length > 1 ? "s" : ""}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
